import log from "logbeacon";

interface EventListener {
  (msg: any): void;
}

export interface Config {
  url: string;
}

class WS {
  ws: WebSocket | null;
  private maxReconnectAttempts = 5; // 最大重连次数
  private reconnectAttempts = 0; // 当前重连次数
  private reconnectTimeout: NodeJS.Timeout | null;
  private config: Config;
  private subscriptions: Map<string, Set<(data: any) => void>> = new Map();
  private connectionCount = 0;
  private updateCallback: EventListener | null;

  constructor(config: Config) {
    this.config = config;
    this.reconnectTimeout = null;
    this.ws = null;
    this.updateCallback = null;
  }

  do_send(msg: string) {
    if (this.ws?.readyState == WebSocket.OPEN) {
      try {
        this.ws.send(msg);
      } catch (e) {
        //ignore error
      }
    }
  }

  private handleOpen() {
    log.debug("[文案]WebSocket 已连接");
    // 连接成功后，重新发送所有已订阅的主题
    this.subscriptions.forEach((_, topic) => {
      this.do_send(topic);
    });
  }

  private handleClose(event: CloseEvent) {
    log.debug("[文案]WebSocket 已关闭:", event);

    if (this.connectionCount > 0) {
      this.reconnect();
    }
  }

  private handleMessage(event: MessageEvent) {
    try {
      if (event.data.startsWith("NF#")) {
        try {
          const msg = JSON.parse(event.data.substr(3));
          // log.debug("line96", msg, this.updateCallback);

          const list = {
            tokenList: [],
            coinmarketcapTokenList: [],
            stockList: [],
          };

          if (msg.channel.startsWith(`pool_trades_`)) {
            list.tokenList = msg.data;
          } else if (msg.channel === "cexpool_trades") {
            list.coinmarketcapTokenList = msg.data;
          } else if (msg.channel.startsWith(`stock_trades`)) {
            list.stockList = msg.data;
          } else {
            log.error("[文案]未知消息类型:", msg);
          }

          if (this.updateCallback) {
            this.updateCallback(list);
          }
        } catch (e) {
          //ignore error from update callback
        }
        return;
      }
    } catch (error) {
      log.error("[文案]消息解析错误:", error);
    }
  }

  private handleError(error: Event) {
    log.error("[文案]WebSocket 错误:", error);
  }

  private initWebSocket() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      this.ws = new WebSocket(this.config.url);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
    } catch (error) {
      log.error("[文案]WebSocket 连接失败:", error);
      this.reconnect();
    }
  }

  // 注册特定主题
  register(topic: string, callback: EventListener) {
    // 取消之前的注册
    this.close();

    // 重新初始化订阅
    this.subscriptions.clear();
    this.connectionCount = 0;

    // 注册新主题
    this.subscriptions.set(topic, new Set([callback]));
    this.connectionCount++;

    // 初始化连接
    this.initWebSocket();
    this.updateCallback = callback;

    // 发送订阅请求
    this.do_send(topic);
  }

  // 取消所有注册
  unregister() {
    this.close();
  }

  // 关闭所有连接
  close() {
    if (this.reconnectTimeout) {
      clearInterval(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.ws?.close();
    this.subscriptions.clear();
    this.connectionCount = 0;
    this.reconnectAttempts = 0;
  }

  private reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      log.error("[文案]达到最大重连次数");
      return;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    log.debug(`${delay / 1000}秒后尝试第${this.reconnectAttempts}次重连...`);

    this.reconnectTimeout = setTimeout(() => {
      this.initWebSocket();
    }, delay);
  }
}

export { WS };
